// src/prototype/components/EAPersonalizationCard.tsx
// ============================================================
// EA PERSONALIZATION CARD
// UPDATES:
// - Increased card height by ~1/3 inch (added padding/margins)
// - Added isLoading prop support
// - Ring color uses DVA orange for in-progress state
// ============================================================

import React from 'react';
import {
  Building2,
  ClipboardList,
  FileUp,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { HelpPopover } from './help/HelpPopover';
import type { HelpKey } from '../help/helpContent';
import type { SurveyFreshnessSummary } from '../utils/surveyFreshness';
import {
  surveyCardLastUpdatedLabel,
  surveyCardStaleBadges,
} from '../utils/surveyFreshness';

export type PersonalizationInputType = 'profile' | 'surveys' | 'documents';
export type PersonalizationStatus = 'not_started' | 'in_progress' | 'complete';

interface EAPersonalizationCardProps {
  type: PersonalizationInputType;
  percentage: number;
  onClick?: () => void;
  className?: string;
  isLoading?: boolean;
  helpKey?: HelpKey;
  /** Surveys card only — module ages from survey response APIs. */
  surveyFreshness?: SurveyFreshnessSummary | null;
}

const cardConfig: Record<PersonalizationInputType, {
  title: string;
  description: string;
  icon: React.ElementType;
  ctaLabels: { not_started: string; in_progress: string; complete: string };
  emphasisLabel?: string;
  bulletItems: string[];
}> = {
  profile: {
    title: 'Organization Profile',
    description: 'Defines scale, constraints, and strategic posture used across all recommendations.',
    icon: Building2,
    ctaLabels: {
      not_started: 'Start Profile',
      in_progress: 'Continue Profile',
      complete: 'Review Profile',
    },
    bulletItems: [
      'Organization basics',
      'Strategic priorities',
    ],
  },
  surveys: {
    title: 'Data Intake Surveys',
    description: 'Establishes priorities, capabilities, and readiness that drive ranking and ROI assumptions.',
    icon: ClipboardList,
    ctaLabels: {
      not_started: 'Start Surveys',
      in_progress: 'Continue Surveys',
      complete: 'Review Surveys',
    },
    emphasisLabel: 'Primary input for ranking & ROI',
    bulletItems: [
      'Strategy assessment',
      'Capabilities review',
      'Data readiness',
    ],
  },
  documents: {
    title: 'Document Upload',
    description: 'Optional, but significantly improves governance alignment and scoring accuracy.',
    icon: FileUp,
    ctaLabels: {
      not_started: 'Upload Documents',
      in_progress: 'Continue Upload',
      complete: 'Manage Documents',
    },
    bulletItems: [
      'Strategic plans',
      'Annual reports',
    ],
  },
};

const getStatus = (percentage: number): PersonalizationStatus => {
  if (percentage === 0) return 'not_started';
  if (percentage >= 100) return 'complete';
  return 'in_progress';
};

const statusStyles: Record<PersonalizationStatus, {
  border: string;
  iconBg: string;
  badgeClass: string;
  badgeLabel: string;
  ringColor: string;
  ctaClass: string;
}> = {
  not_started: {
    border: 'border-[#e5e7eb]',
    iconBg: 'bg-[#F5F7FA]',
    badgeClass: 'bg-[#F5F7FA] text-[#717182]',
    badgeLabel: 'Not started',
    ringColor: '#e5e7eb',
    ctaClass: 'bg-[#18365E] text-white hover:bg-[#18365E]/90',
  },
  in_progress: {
    border: 'border-[#FF6E4C]/30',
    iconBg: 'bg-[#FF6E4C]/10',
    badgeClass: 'bg-[#FF6E4C]/10 text-[#FF6E4C]',
    badgeLabel: 'In progress',
    ringColor: '#FF6E4C',
    ctaClass: 'bg-[#18365E] text-white hover:bg-[#18365E]/90',
  },
  complete: {
    border: 'border-green-200',
    iconBg: 'bg-green-50',
    badgeClass: 'bg-green-50 text-green-700',
    badgeLabel: 'Complete',
    ringColor: '#10B981',
    ctaClass: 'bg-[#18365E] text-white hover:bg-[#18365E]/90',
  },
};

export function EAPersonalizationCard({
  type,
  percentage,
  onClick,
  className = '',
  isLoading = false,
  helpKey,
  surveyFreshness = null,
}: EAPersonalizationCardProps) {
  const config = cardConfig[type];
  const Icon = config.icon;
  const status = getStatus(percentage);
  const styles = statusStyles[status];
  const displayPercentage = Math.min(percentage, 100);
  const lastUpdatedLabel = type === 'surveys' && surveyFreshness
    ? surveyCardLastUpdatedLabel(surveyFreshness)
    : null;
  const staleBadges = type === 'surveys' && surveyFreshness
    ? surveyCardStaleBadges(surveyFreshness)
    : [];

  // Calculate ring progress
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (displayPercentage / 100) * circumference;

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-xl border p-5
        hover:shadow-md hover:border-[#18365E]/25 cursor-pointer transition-all duration-200
        flex flex-col h-full
        ${styles.border}
        ${className}
      `}
    >
      {/* Header with Icon and Status Badge */}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${styles.iconBg}`}>
          <Icon className="w-5 h-5 text-[#18365E]" />
        </div>

        <div className="flex items-center gap-1.5">
          {status === 'complete' && (
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          )}
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles.badgeClass}`}>
            {isLoading ? 'Loading...' : styles.badgeLabel}
          </span>
        </div>
      </div>

      {/* Title with optional emphasis label */}
      <div className="mb-2">
        <h3 className="text-base font-bold text-[#18365E] flex items-center gap-1.5">
          {config.title}
          {helpKey && (
            <span onClick={(e) => e.stopPropagation()}>
              <HelpPopover helpKey={helpKey} iconSize={13} />
            </span>
          )}
        </h3>
        {config.emphasisLabel && (
          <p className="text-[10px] font-medium text-[#717182]">
            {config.emphasisLabel}
          </p>
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-[#717182] mb-4 flex-1 leading-snug">
        {config.description}
      </p>

      {/* Progress Ring */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative w-[72px] h-[72px] shrink-0">
          {isLoading ? (
            <div className="w-[72px] h-[72px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#e5e7eb] border-t-[#FF6E4C]"></div>
            </div>
          ) : (
            <>
              <svg className="w-[72px] h-[72px] -rotate-90">
                <circle
                  cx="36"
                  cy="36"
                  r={radius}
                  stroke="#e5e7eb"
                  strokeWidth="6"
                  fill="none"
                />
                <circle
                  cx="36"
                  cy="36"
                  r={radius}
                  stroke={styles.ringColor}
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-700"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-[#18365E]">
                  {displayPercentage}%
                </span>
              </div>
            </>
          )}
        </div>

        <div className="flex-1 text-xs text-[#717182] space-y-0.5">
          {config.bulletItems.map((item) => (
            <p key={item}>• {item}</p>
          ))}
        </div>
      </div>

      {/* Status badges — reserved height keeps Profile / Surveys / Upload aligned */}
      <div className="min-h-[28px] mb-3 flex flex-wrap items-center gap-1.5">
        {lastUpdatedLabel && (
          <span
            title="Most recent answer across strategy, capabilities, and readiness"
            className="inline-flex items-center gap-1 rounded-full bg-[#F5F7FA] px-2 py-0.5 text-[11px] font-semibold text-[#4b5563]"
          >
            <Clock className="w-3 h-3 text-[#717182] shrink-0" />
            {lastUpdatedLabel}
          </span>
        )}
        {staleBadges.map((badge) => (
          <span
            key={badge.type}
            role="status"
            title={`${badge.label} — ${badge.daysOld} days old (max 180 days)`}
            className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
          >
            <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" />
            {badge.label}
          </span>
        ))}
      </div>

      <button
        className={`
          mt-auto w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
          text-sm font-semibold transition-all
          ${styles.ctaClass}
        `}
        disabled={isLoading}
      >
        {isLoading ? 'Loading...' : config.ctaLabels[status]}
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default EAPersonalizationCard;
